import React, { FC, useCallback, useLayoutEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import ContentBaseLayout from '../../../../yourborrowbox/libx/shared/ContentBaseLayout';
import { FilterBar } from '../../../../shared/common/FilterBar';
import NoElementsFound, { NO_ELEMENTS_PRODUCTS } from '../../../../shared/components/NoElementsFound';
import ProductList from '../../../../shared/common/ProductList';
import CatalogueListItem from '../../../catalogue/CatalogueListItem';
import Pagination, { getPaginationStateObject } from '../../../../shared/common/Pagination';
import {
    deselectOne,
    isInSelectionMode,
    isSelected,
    selectOne,
    setSelectionMode
} from '../../../../store/productSelection/actions';
import { LanguageProvider } from '../../../../contexts/language';
import { CatalogueProductTile } from '../../../catalogue/CatalogueProductTile';
import { BulkActionButtons } from '../../../../shared/search/BulkActionButtons';
import { AppState } from '../../../../store/appState';
import { Orderlist } from '../../../../../models/OrderList';
import { ProductSorting } from '../../../../../models/Product';
import { ageCategoryFromString } from '../../../../../models/AgeCategory';
import { ALL_LANGUAGES } from '../../constants';
import { useHistory, useLocation, useParams } from 'react-router';
import { AppDispatch } from '../../../../store';
import { PriceTier } from '../../../../../models/PriceTier';
import { displayLanguageLearningProducts } from '../../../../store/header/actions';
import queryString from 'query-string';
import { useQueryParams } from '../../../../shared/hooks/useQueryParams';
import { deserializeFilter } from '../../../../shared/utils/deserializeFilter';
import { useOrderLists } from '../../../../shared/common/useOrderLists';
import { modifyURLState, modifyURLStates } from '../../../../shared/utils/utils-routing';
import { loanFormatKeyToObject } from '../../../../store/loanFormat/actions';
import { invalidateLanguageProducts, useLanguageProducts } from '../../../../api/languages/useLanguageProducts';
import { ResponsiveFilterBar } from '../../../../shared/filter/ResponsiveFilterBar';
import { fetchLanguageLearningAggregationsQuickSearch } from '../../../../api/languages/fetchLanguageLearningAggregationsQuickSearch';
import { FetchAggregations } from '../../../../api/listContent/fetchAggregations';
import { fetchLanguageLearningAggregations } from '../../../../api/languages/fetchLanguageLearningAggregations';
import { QueryKeyBuilder } from '../../../../shared/filter/useAggregationsQuery';
import { FetchAggregationsQuickSearch } from '../../../../api/listContent/fetchAggregationsQuickSearch';
import { defaultVisibleFilterGroups } from '../../../../shared/filter/defaultVisibleFilterGroups';
import { useTranslation } from 'react-i18next';
import { ExcludeProductSelection } from '../../../../shared/product-selection/ProductSelectionTypes';
import { useAddFromForeignLanguageToCart } from '../../../../api/shopping-cart/useAddFromForeignLanguageToCart';
import { LanguageScope } from '../../useLanguages';
import { useAddFromForeignLanguageToList } from '../../../../api/selection-list/useAddFromForeignLanguageToList';
import { MAX_SELECT } from '../../../../shared/product-selection/useProductSelection';
import { showNotification, TYPE_WARNING } from '../../../../store/notifications/actions';
import { useAddToList } from '../../../../api/selection-list/useAddSingleToList';
import { useAddToCart } from '../../../../api/shopping-cart/useAddToCart';
import { CheckBeforeAddingModal } from '../../../../shared/common/CheckBeforeAddingModal';
import { SingleAddOptions } from '../../../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../../../models/ListActionTarget';
import { useListContentChecks } from '../../../../shared/common/useListContentChecks';

const StFilterBar = styled(FilterBar)`
    margin: 52px 0 40px 0;
`;

const SORT_BY_QUERY_PARAM = 'sortBy';
const PAGE_QUERY_PARAM = 'page';

export const DEFAULT_PAGE_SIZE = 60;

export const LanguageLearningProductsPage: FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { t } = useTranslation();
    const history = useHistory();
    const { search } = useLocation();
    const authentication = useSelector((state: AppState) => state.authentication);
    const productSelection = useSelector((state: AppState) => state.productSelection);
    const {
        languageId: selectedLanguage,
        loanFormat: loanFormatPathParam,
        shopLanguage
    } = useParams<{
        languageId: string;
        loanFormat: string;
        shopLanguage?: string;
    }>();

    useLayoutEffect(() => {
        dispatch(displayLanguageLearningProducts());
    }, [dispatch]);

    const documentTitle = t('shop.language.languageLearningAllProducts');
    const loanFormat = loanFormatKeyToObject(loanFormatPathParam);
    const language = selectedLanguage === ALL_LANGUAGES ? undefined : decodeURIComponent(selectedLanguage!);

    /**
     * The `displayFilter` query param is either there or not. There is no value attached to it.
     * Examples:
     *      with:       `/collection/by-genre/all?displayFilter
     *      without:    `/collection/by-genre/all
     *
     * Parsing this query param with the `query-string` library will result in either null value or undefined.
     * This is the way how we can identify if the filter should be open or not.
     * Unfortunately, we can not replicate this behaviour with the `useQueryParams` hook.
     *
     * Of course it would be nicer to have a boolean value.
     * But that is a breaking change in regard to the URL structure.
     */
    const { displayFilter: displayFilterQueryParam } = queryString.parse(search);
    const displayFilter = displayFilterQueryParam === null;

    const {
        page: { value: pageParam = 1 },
        pageSize: { value: pageSizeParam },
        sortBy: { value: sortByParam },
        filter: { value: filterQueryParam },
        ageCategory: { value: ageCategoryQueryParam },
        display: { value: display }
    } = useQueryParams({
        [PAGE_QUERY_PARAM]: 'integer',
        [SORT_BY_QUERY_PARAM]: 'string',
        pageSize: 'integer',
        filter: 'string',
        ageCategory: 'string',
        display: 'string'
    });
    const page = pageParam - 1 ?? 0;
    const sortBy = sortByParam ?? ProductSorting.RELEASE_DATE_NEW_TO_OLD;
    const pageSize = pageSizeParam ?? DEFAULT_PAGE_SIZE;
    const ageCategory = ageCategoryFromString(ageCategoryQueryParam);
    const filter = deserializeFilter(filterQueryParam);

    const { orderlists, createNewList } = useOrderLists();

    const ageCategoryString = ageCategoryFromString(ageCategory!);
    const { data: productData, isLoading: isScopeLoading } = useLanguageProducts({
        loanFormat: loanFormat.requestKey,
        ageCategory: ageCategoryString,
        filterMap: filter,
        pagination: {
            page,
            pageSize: DEFAULT_PAGE_SIZE,
            sortBy
        },
        language,
        isLanguageLearningScope: true,
        shopLanguage
    });

    const products = productData.products;
    const totalProducts = productData.total;

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(
        () => defaultVisibleFilterGroups(authentication, ageCategory),
        [ageCategory, authentication]
    );
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => [
            'languageLearningProductsPage',
            language,
            'aggregation',
            loanFormat.requestKey!,
            ageCategory,
            filters,
            expandedFilterGroups,
            shopLanguage
        ],
        [ageCategory, language, loanFormat.requestKey, shopLanguage]
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options =>
            fetchLanguageLearningAggregations({
                ...options,
                language,
                loanFormat: loanFormat.requestKey || undefined,
                ageCategory,
                shopLanguage
            }),
        [ageCategory, language, loanFormat.requestKey, shopLanguage]
    );
    const fetchAggregationsQuickSearchCallback = useCallback<FetchAggregationsQuickSearch>(
        options =>
            fetchLanguageLearningAggregationsQuickSearch({
                ...options,
                language,
                loanFormat: loanFormat.requestKey!,
                ageCategory,
                shopLanguage
            }),
        [ageCategory, language, loanFormat.requestKey, shopLanguage]
    );
    //endregion

    const setSortBy = nextSortBy => {
        const pageQueryState = getPaginationStateObject();
        const sortByQueryState = { name: SORT_BY_QUERY_PARAM, value: nextSortBy, encode: false };

        const nextSearch = modifyURLStates(history.location.search, [pageQueryState, sortByQueryState]);

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    const setPage = nextPage => {
        const nextSearch = modifyURLState(
            history.location.search,
            PAGE_QUERY_PARAM,
            nextPage !== 0 ? nextPage + 1 : undefined,
            false
        );

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };
    const { addToList } = useAddToList();
    const { addToCart } = useAddToCart(() => {
        invalidateLanguageProducts(true).then();
    });

    const onAction = useCallback(
        (performChecks: boolean, options: SingleAddOptions) => {
            if (options.target === ListActionTarget.List) {
                return addToList(options.options, options.listId, performChecks);
            } else {
                return addToCart(options.options, performChecks);
            }
        },
        [addToCart, addToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps } = useListContentChecks<SingleAddOptions>({ onAction });

    function onFilterDisplayToggle() {
        const nextSearch = modifyURLState(history.location.search, 'displayFilter', !displayFilter, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    function onDisplayChange(nextDisplayType) {
        const nextSearch = modifyURLState(history.location.search, 'display', nextDisplayType, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    const { isLoading: isLoadingAddProductsToCartByFilter, addFromForeignLanguageToCartBulk } =
        useAddFromForeignLanguageToCart({
            loanFormat,
            filter: filterQueryParam ?? '',
            ageCategory,
            language,
            source: LanguageScope.LANGUAGE_LEARNING,
            callback: () => {
                invalidateLanguageProducts(true).then();
            },
            shopLanguage
        });

    const { isLoading: isLoadingAddProductsToListByFilter, addFromForeignLanguageToListBulk } =
        useAddFromForeignLanguageToList({
            loanFormat,
            filter: filterQueryParam ?? '',
            ageCategory,
            language,
            source: LanguageScope.LANGUAGE_LEARNING,
            shopLanguage
        });

    const onBulkAddToList = async (list: Orderlist, priceTier: PriceTier, performChecks: boolean) => {
        const productSelection: ExcludeProductSelection = {
            excluded: [],
            totalCount: productData.total ?? 0
        };

        if (productSelection.totalCount > MAX_SELECT) {
            dispatch(
                showNotification(t('notifications.useProductSelection.error', { maxSelect: MAX_SELECT }), TYPE_WARNING)
            );
            throw new Error('max selection exceeded');
        }

        return addFromForeignLanguageToListBulk(list.id, priceTier, performChecks, productSelection);
    };

    const onBulkAddToCart = async (priceTier: PriceTier, performChecks: boolean) => {
        const productSelection: ExcludeProductSelection = {
            excluded: [],
            totalCount: productData.total ?? 0
        };

        if (productSelection.totalCount > MAX_SELECT) {
            dispatch(
                showNotification(t('notifications.useProductSelection.error', { maxSelect: MAX_SELECT }), TYPE_WARNING)
            );
            throw new Error('max selection exceeded');
        }

        return addFromForeignLanguageToCartBulk(priceTier, performChecks, productSelection);
    };

    const loading = isScopeLoading || isLoadingAddProductsToCartByFilter || isLoadingAddProductsToListByFilter;

    return (
        <LanguageProvider value={language ?? ''}>
            <ContentBaseLayout
                topComponent={
                    <>
                        <BulkActionButtons
                            listLabel={t('addToList')}
                            cartLabel={t('addToCart')}
                            theme="language"
                            orderlists={orderlists}
                            onCreateNewListClicked={createNewList}
                            onBulkAddToList={onBulkAddToList}
                            onBulkAddToCart={onBulkAddToCart}
                        />

                        <StFilterBar
                            totalProducts={totalProducts}
                            onSortChange={setSortBy}
                            dispatch={dispatch}
                            currentPage={page}
                            pageSize={pageSize}
                            onPageChange={setPage}
                            hideGroupBy={true}
                            hideSelectButtons={true}
                            displayType={display}
                            sortBy={sortBy}
                            onFilterDisplayToggle={onFilterDisplayToggle}
                            onDisplayChange={onDisplayChange}
                            toggleSelectionMode={() => dispatch(setSelectionMode(!isInSelectionMode(productSelection)))}
                            displayFilter={displayFilter}
                        />
                    </>
                }
                sidebarComponent={
                    displayFilter ? (
                        <ResponsiveFilterBar
                            visibleFilterGroups={visibleFilterGroups}
                            buildQueryKey={aggregationsQueryKeyBuilder}
                            fetchAggregations={fetchAggregations}
                            fetchAggregationsQuickSearch={fetchAggregationsQuickSearchCallback}
                        />
                    ) : null
                }
                contentComponent={
                    <div className="ca-main">
                        {products.length === 0 && !loading ? (
                            <NoElementsFound type={NO_ELEMENTS_PRODUCTS} />
                        ) : (
                            <div className="ca-main-inner">
                                <ProductList
                                    loading={loading}
                                    products={products}
                                    linkToAllLanguagePage={false}
                                    displayType={display}
                                    forceStoreLoanFormat={true}
                                    listItemComponent={CatalogueListItem}
                                    tileItemComponent={CatalogueProductTile}
                                    onAddPrice={changePrice}
                                    isProductSelectable={false}
                                    onSelectProduct={productId =>
                                        !isSelected(productId, productSelection)
                                            ? dispatch(selectOne(productId))
                                            : dispatch(deselectOne(productId))
                                    }
                                    currentPage={page}
                                />

                                <Pagination
                                    totalItems={totalProducts}
                                    onPageChange={setPage}
                                    currentPage={page}
                                    pageSize={pageSize}
                                />
                            </div>
                        )}
                    </div>
                }
                documentTitle={documentTitle}
                loading={loading}
            />

            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
        </LanguageProvider>
    );
};
